import React, { useEffect, useState } from 'react';

import { useStyles } from './style';
import { useLocation } from 'react-router';
import useSearch from '@tcs/client-portal-common/hooks/useSearch';
import { Typography } from '@forgeglobal/design-framework';
import { useNavigate } from 'react-router-dom';
import { RetrievedSearchItemType } from '@tcs/client-portal-common/components/molecules/Search/types';
import { SearchType } from '@tcs/client-portal-api/types/SearchType';
import SearchTable from '@tcs/client-portal-common/components/molecules/SearchTable';
import BaseHeader from '@tcs/client-portal-common/components/molecules/Table/cells/headers/BaseHeader';
import HighlightableCell from '@tcs/client-portal-common/components/molecules/Table/cells/HighlightableCell';
import BaseCell from '@tcs/client-portal-common/components/molecules/Table/cells/BaseCell';
import DebitCreditCell from '@tcs/client-portal-common/components/molecules/Table/cells/DebitCreditCell';
import orderBy from 'lodash/orderBy';

const ROUTES = {
  [SearchType.Account]: 'accounts',
  [SearchType.Representative]: 'Representatives',
};

const SearchResults = () => {
  const classes = useStyles();
  const { state } = useLocation() as any;
  const nav = useNavigate();
  const [sortByField] = useState('accountStatus');
  const [sortDirection, setSortDirection] = useState('asc');

  // fallback in case of no location object on hard reload
  const initialValue = state?.searchValue ?? '';
  const type = state?.searchType;

  const [value, setValue] = useState(initialValue);
  const [page, setPage] = useState(1);

  const onSearch = (tableValue) => {
    setPage(page);
    setValue(tableValue);
  };

  useEffect(() => {
    setValue(state.searchValue);
    //default page to 1 when search value changes
    setPage(1);
  }, [state.searchValue]);

  const { data, loading } = useSearch(
    value,
    type,
    page,
    undefined,
    true,
    false,
  );

  const onClickTableRow = (item: RetrievedSearchItemType) => {
    if (
      type === SearchType.Account &&
      item.isExistingAccount === false &&
      item.applicationId
    ) {
      nav(`/applications/${item.applicationId}`, {
        state: { searchValue: value },
      });
      return;
    }

    nav(`/${ROUTES[type]}/${item.id}`);
  };

  let orderedAccounts = data ? data[0].data : [];

  const onClickTableColumn = (id: string) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  };

  if (sortDirection === 'asc') {
    orderedAccounts = orderBy(orderedAccounts, (a) => a[sortByField], ['asc']);
  }

  if (sortDirection === 'desc') {
    orderedAccounts = orderBy(orderedAccounts, (a) => a[sortByField], ['desc']);
  }

  const SEARCH_RESULTS_INFO = {
    [SearchType.Account]: {
      title: 'Account Holders',
      placeholder: 'Search for name or account number',
      columns: [
        {
          accessor: 'name',
          header: () => <BaseHeader text="Name" />,
          cell: (info) => (
            <HighlightableCell value={info.getValue()} search={value} />
          ),
          size: 300,
        },
        {
          accessor: 'type',
          header: () => <BaseHeader text="Account Type" />,
          cell: (info) => <BaseCell value={info.getValue()} />,
          size: 150,
        },
        {
          accessor: 'id',
          header: () => <BaseHeader text="Account Number" />,
          cell: (info) => (
            <HighlightableCell value={info.getValue()} search={value} />
          ),
          size: 150,
        },
        {
          accessor: 'accountStatus',
          header: () => <BaseHeader text="Account Status" />,
          cell: (info) => <BaseCell value={info.getValue()} />,
          size: 150,
        },
        {
          accessor: 'availableCash',
          header: () => <BaseHeader text="Custodial Cash" align="right" />,
          cell: (info) => <DebitCreditCell value={info.getValue()} />,
          size: 150,
        },
        {
          accessor: 'investmentValue',
          header: () => <BaseHeader text="Investment Value" align="right" />,
          cell: (info) => <DebitCreditCell value={info.getValue()} />,
          size: 150,
        },
      ],
    },
    [SearchType.Representative]: {
      title: 'Representatives',
      placeholder: 'Search for name or representative code',
      columns: [
        {
          accessor: 'name',
          header: () => <BaseHeader text="Name" />,
          cell: (info) => (
            <HighlightableCell value={info.getValue()} search={value} />
          ),
          size: 300,
        },
        {
          accessor: 'id',
          header: () => <BaseHeader text="Representative Code" />,
          cell: (info) => (
            <HighlightableCell value={info.getValue()} search={value} />
          ),
          size: 150,
        },
        {
          accessor: 'numberOfAccounts',
          header: () => <BaseHeader text="Number of Accounts" />,
          cell: (info) => <BaseCell value={info.getValue()} />,
          size: 150,
        },
      ],
    },
  };

  if (!SEARCH_RESULTS_INFO[type]) {
    return null;
  }

  if (!data) return null;

  const filteredActiveAccounts = new Array(1).fill({
    data: orderedAccounts,
    totalCount: data[0].totalCount,
  });

  return (
    <div className={classes.wrapper}>
      <Typography variant="h5" className={classes.title} data-test-id="title">
        {SEARCH_RESULTS_INFO[type].title}
      </Typography>
      <SearchTable
        searchValue={value}
        searchPlaceholder={SEARCH_RESULTS_INFO[type].placeholder}
        onSearch={onSearch}
        data={(filteredActiveAccounts && filteredActiveAccounts[0]?.data) || []}
        columns={SEARCH_RESULTS_INFO[type].columns}
        onRowClick={onClickTableRow}
        onColumnClick={onClickTableColumn}
        totalItems={
          (filteredActiveAccounts && filteredActiveAccounts[0]?.totalCount) || 0
        }
        page={page}
        setPage={setPage}
        loading={loading}
      />
    </div>
  );
};

export default SearchResults;
